import {
  AccountsForRunningContestsResponse,
  ClosedContestWithWinners,
  ContestInformation,
  PaginationResult,
  UserContestRanking,
} from '@monorepo/types';
import {
  QUERY_CONFIGS,
  QueryConfigKeys,
} from '@hub/utils/react-query/query-config';
import {
  fetchAccountsForRunningContests,
  fetchClosedContestWithWinners,
  fetchContestInformation,
  fetchUserContestRanking,
} from '@hub/utils/react-query/query-functions';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { ExtractFnReturnType } from '@hub/utils/react-query/react-query';
import {
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';

const CONFIG_KEY = QueryConfigKeys.CONTEST_CATEGORY;

export const useAccountsForRunningContests = (
  options?: UseQueryOptions<
    ExtractFnReturnType<typeof fetchAccountsForRunningContests>,
    Error,
    AccountsForRunningContestsResponse
  >,
) => {
  return useQuery<ExtractFnReturnType<typeof fetchAccountsForRunningContests>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].LIVE_CONTESTANTS,
    queryFn: fetchAccountsForRunningContests,
    staleTime: QUERY_CONFIGS[QueryConfigKeys.CONTEST_CATEGORY].staleTime,
    ...options,
  });
};

export const useClosedContestWithWinners = (
  options?: UseQueryOptions<
    PaginationResult<ClosedContestWithWinners>,
    Error,
    ClosedContestWithWinners[]
  >,
) => {
  return useQuery<
    PaginationResult<ClosedContestWithWinners>,
    Error,
    ClosedContestWithWinners[]
  >({
    queryKey: QUERY_KEYS[CONFIG_KEY].CLOSED_WINNERS,
    queryFn: () => fetchClosedContestWithWinners(1),
    select: (data) => data.results,
    staleTime: QUERY_CONFIGS[QueryConfigKeys.CONTEST_CATEGORY].staleTime,
    ...options,
  });
};

export const useUserContestRanking = (
  options?: UseQueryOptions<
    ExtractFnReturnType<typeof fetchUserContestRanking>,
    Error,
    UserContestRanking[]
  >,
) => {
  return useQuery<ExtractFnReturnType<typeof fetchUserContestRanking>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].USER_CONTEST_RANKING,
    queryFn: fetchUserContestRanking,
    staleTime: QUERY_CONFIGS[QueryConfigKeys.CONTEST_CATEGORY].staleTime,
    ...options,
  });
};

export const useInfiniteClosedContestWithWinners = () => {
  return useInfiniteQuery<PaginationResult<ClosedContestWithWinners>, Error>({
    queryKey: QUERY_KEYS[CONFIG_KEY].CLOSED_WINNERS_INFINITE,
    queryFn: ({ pageParam = 1 }) =>
      fetchClosedContestWithWinners(pageParam as number),
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0,
      );
      return totalLoaded < lastPage.total ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: QUERY_CONFIGS[QueryConfigKeys.CONTEST_CATEGORY].staleTime,
  });
};

export const useClosedContestsWithUserRanking = () => {
  const closedContests = useInfiniteClosedContestWithWinners();
  const userRanking = useUserContestRanking();
  const allContests =
    closedContests.data?.pages.flatMap((page) => page.results) ?? [];

  return {
    closedContests: {
      ...closedContests,
      data: allContests,
    },
    userRanking,
    isLoading: closedContests.isLoading || userRanking.isLoading,
    isError: closedContests.isError || userRanking.isError,
    error: closedContests.error || userRanking.error,
    hasNextPage: closedContests.hasNextPage,
    fetchNextPage: closedContests.fetchNextPage,
    isFetchingNextPage: closedContests.isFetchingNextPage,
  };
};

export const useContestInformation = (
  options?: UseQueryOptions<
    ExtractFnReturnType<typeof fetchContestInformation>,
    Error,
    ContestInformation
  >,
) => {
  return useQuery<ExtractFnReturnType<typeof fetchContestInformation>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].CONTEST_INFORMATION,
    queryFn: fetchContestInformation,
    staleTime: QUERY_CONFIGS[QueryConfigKeys.CONTEST_CATEGORY].staleTime,
    ...options,
  });
};
